import React, { useMemo } from "react";
import { useKnowledgeProgress, setPath, clearProgress, type KnowledgePath } from "@/lib/knowledge/progress";
import { KNOWLEDGE_PAGES } from "@/lib/knowledge/pages";
import { Link } from "@/components/common/Link";
import { Compass, MapPin, Wrench, RotateCcw, CheckCircle2, ArrowRight, ShieldCheck, Zap } from "lucide-react";
interface PathDefinition {
  id: KnowledgePath;
  title: string;
  badge: string;
  summary: string;
  icon: typeof Compass;
  idealFor: string;
}
const PATHS: PathDefinition[] = [{
  id: "guided",
  title: "Guided Discovery",
  badge: "Curated Sequence",
  summary: "A structured pedagogical journey moving from philosophical foundations through formal logic, critical analysis, fallacy detection, to epistemic justification.",
  icon: Compass,
  idealFor: "Learners seeking a cohesive, step-by-step intellectual scaffold."
}, {
  id: "free",
  title: "Free Exploration",
  badge: "Non-Linear Matrix",
  summary: "Direct open access to every topic laboratory, reference archive, and epistemic taxonomy without prescriptive sequence.",
  icon: MapPin,
  idealFor: "Researchers and autodidacts navigating specific questions."
}, {
  id: "practical",
  title: "Practical Investigation",
  badge: "Hands-on Instruments",
  summary: "Jump straight to interactive tools: truth tables, premise reconstructors, fallacy specimen analyzers, and evidence calibrators.",
  icon: Wrench,
  idealFor: "Thinkers who want immediate diagnostic instruments for everyday claims."
}];
const GUIDED_SEQUENCE = [{
  pageId: "philosophy",
  title: "Philosophy as Investigation",
  route: "/PhilosophyAsInvestigationPage",
  role: "Foundational framing & thought experiments"
}, {
  pageId: "logic",
  title: "Logic Laboratory",
  route: "/LogicLaboratoryPage",
  role: "Formal inference rules & deductive validity"
}, {
  pageId: "critical",
  title: "Critical Thinking Workshop",
  route: "/CriticalThinkingPage",
  role: "Dissecting real-world claims & steelmanning"
}, {
  pageId: "fallacies",
  title: "Fallacy Library",
  route: "/FallacyLibraryPage",
  role: "Cataloguing recurring errors & cognitive traps"
}, {
  pageId: "epistemology",
  title: "Knowledge Laboratory",
  route: "/KnowledgeLaboratoryPage",
  role: "Theories of justified belief & response to skepticism"
}];
const PRACTICAL_TOOLS = [{
  title: "Truth-Table Simulator",
  location: "Logic Laboratory",
  route: "/LogicLaboratoryPage#truth-table",
  description: "Test propositional formulas, evaluate tautologies, and verify deductive validity."
}, {
  title: "Claim Analyzer",
  location: "Critical Thinking Workshop",
  route: "/CriticalThinkingPage#claim-analyzer",
  description: "Break complex claims into premises, uncover unstated assumptions, and audit evidentiary backing."
}, {
  title: "Argument Reconstructor",
  location: "Critical Thinking Workshop",
  route: "/CriticalThinkingPage#argument-reconstructor",
  description: "Standardize rhetorical discourse into clear deductive and inductive premise-conclusion sets."
}, {
  title: "Fallacy Identification Lab",
  location: "Fallacy Library",
  route: "/FallacyLibraryPage#identification-lab",
  description: "Diagnose subtle informal fallacies and cognitive distortions across realistic scenarios."
}, {
  title: "Evidence Comparison Matrix",
  location: "Knowledge Laboratory",
  route: "/KnowledgeLaboratoryPage#evidence-comparison",
  description: "Audit justification standards across foundationalist, coherentist, and reliabilist criteria."
}];
export function ThreeExplorationPaths() {
  const {
    path: activePath,
    visited,
    lastPageId
  } = useKnowledgeProgress();

  // Find the next recommended page in the guided sequence
  const nextGuidedPage = useMemo(() => {
    const unvisited = GUIDED_SEQUENCE.find(step => !visited.includes(step.pageId));
    if (unvisited) return unvisited;
    if (lastPageId) {
      const match = GUIDED_SEQUENCE.find(step => step.pageId === lastPageId);
      if (match) return match;
    }
    return GUIDED_SEQUENCE[0];
  }, [visited, lastPageId]);
  return <section id="exploration-paths" className="w-full scroll-mt-24 min-w-0">
      {/* Header */}
      <div className="mb-8">
        <span className="ql-eyebrow mb-2">CHOOSE YOUR APPROACH</span>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
          Three Ways to Explore
        </h2>
        <p className="font-sans text-sm sm:text-base text-muted-foreground mt-1 max-w-2xl">
          Select an intellectual mode suited to your immediate goals. Your choice persists across sessions locally in your browser.
        </p>
      </div>

      {/* Path Selector Tabs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 min-w-0">
        {PATHS.map(p => {
        const Icon = p.icon;
        const isSelected = activePath === p.id;
        return <button key={p.id} type="button" onClick={() => setPath(p.id)} aria-pressed={isSelected} className={`aok-path-card text-left p-5 rounded-xl border flex flex-col justify-between h-full min-h-[160px] min-w-0 group focus-visible:ring-2 focus-visible:ring-ring ${isSelected ? "active bg-card/80 border-primary" : "border-border/60 bg-card/40 hover:bg-card/70"}`}>
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-2 mb-3 min-w-0 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 font-mono text-xs px-2.5 py-0.5 rounded-full border max-w-full min-w-0 ${isSelected ? "text-primary border-primary/40 bg-primary/10" : "text-muted-foreground border-border/50 bg-muted/40"}`}>
                    <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="break-words">{p.badge}</span>
                  </span>
                  {isSelected && <span className="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_var(--glow-primary)] shrink-0" />}
                </div>

                <h3 className="font-serif text-lg font-bold text-foreground group-hover:text-primary transition-colors break-words leading-snug">
                  {p.title}
                </h3>

                <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed break-words min-w-0">
                  {p.summary}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-border/30 text-[0.75rem] font-mono text-muted-foreground/80 break-words min-w-0">
                {p.idealFor}
              </div>
            </button>;
      })}
      </div>

      {/* Active Path Content Area */}
      <div className="ql-glass p-6 sm:p-8 rounded-2xl border border-border min-w-0">
        {activePath === "guided" && <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/40">
              <div>
                <h3 className="font-serif text-xl font-bold text-foreground">
                  Guided Discovery Sequence
                </h3>
                <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-1">
                  Follow the logical progression through the five Phase One investigation labs.
                </p>
              </div>

              {/* Continue where you left off button */}
              <Link to={nextGuidedPage.route} className="ql-neon-button inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs sm:text-sm font-serif font-bold uppercase tracking-wider min-h-[44px] shrink-0">
                <span>Continue where you left off</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Step list */}
            <div className="space-y-3">
              {GUIDED_SEQUENCE.map((step, idx) => {
            const isVisited = visited.includes(step.pageId);
            return <Link key={step.pageId} to={step.route} className="p-4 rounded-xl border border-border/50 bg-card/40 hover:bg-card/75 hover:border-primary/50 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-h-[44px] group min-w-0">
                    <div className="flex items-center gap-3.5 min-w-0 flex-1">
                      <span className="font-mono text-xs font-bold text-primary w-6 shrink-0">
                        0{idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <h4 className="font-serif text-sm sm:text-base font-bold text-foreground group-hover:text-primary transition-colors break-words leading-snug">
                            {step.title}
                          </h4>
                          {isVisited && <span className="inline-flex items-center gap-1 font-mono text-[0.65rem] text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2 py-0.5 rounded-full shrink-0">
                              <CheckCircle2 className="w-2.5 h-2.5" />
                              Explored
                            </span>}
                        </div>
                        <p className="font-sans text-xs text-muted-foreground mt-0.5 break-words min-w-0">
                          {step.role}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-xs font-mono text-primary font-medium shrink-0 self-end sm:self-auto">
                      <span>Enter Lab</span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>;
          })}
            </div>
          </div>}

        {activePath === "free" && <div className="space-y-6">
            <div className="pb-4 border-b border-border/40">
              <h3 className="font-serif text-xl font-bold text-foreground">
                Free Exploration Matrix
              </h3>
              <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-1">
                All 6 Phase One modules and master archives are open for self-directed study in any order.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0">
              {KNOWLEDGE_PAGES.filter(p => p.id !== "hub").map(page => {
            const isVisited = visited.includes(page.id);
            return <Link key={page.id} to={page.route} title={page.title} className="p-4 rounded-xl border border-border/50 bg-card/40 hover:bg-card/75 hover:border-primary/50 transition-all flex flex-col justify-between h-full min-h-[130px] group min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-2 min-w-0 flex-wrap">
                        <span className="font-mono text-xs text-primary font-bold uppercase shrink-0">
                          {page.id}
                        </span>
                        {isVisited && <span className="inline-flex items-center gap-1 font-mono text-[0.65rem] text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2 py-0.5 rounded-full shrink-0">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            Visited
                          </span>}
                      </div>
                      <h4 className="font-serif text-sm sm:text-base font-bold text-foreground group-hover:text-primary transition-colors break-words leading-snug">
                        {page.title}
                      </h4>
                      <p className="font-sans text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2 break-words min-w-0">
                        {page.blurb}
                      </p>
                    </div>

                    <div className="mt-3 flex items-center gap-1 text-xs font-mono text-primary font-medium shrink-0">
                      <span>Explore</span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>;
          })}
            </div>
          </div>}

        {activePath === "practical" && <div className="space-y-6">
            <div className="pb-4 border-b border-border/40">
              <h3 className="font-serif text-xl font-bold text-foreground">
                Hands-on Diagnostic Instruments
              </h3>
              <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-1">
                Direct anchor links into the interactive workbench tools built across the Phase One laboratories.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
              {PRACTICAL_TOOLS.map((tool, idx) => <Link key={idx} to={tool.route} className="p-4 rounded-xl border border-border/50 bg-card/40 hover:bg-card/75 hover:border-primary/50 transition-all flex flex-col justify-between h-full min-h-[120px] group min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5 min-w-0 flex-wrap">
                      <span className="font-serif text-sm sm:text-base font-bold text-foreground group-hover:text-primary transition-colors break-words leading-snug">
                        {tool.title}
                      </span>
                      <span className="font-mono text-[0.7rem] text-primary bg-primary/10 border border-primary/30 px-2 py-0.5 rounded-full shrink-0">
                        {tool.location}
                      </span>
                    </div>
                    <p className="font-sans text-xs text-muted-foreground leading-relaxed break-words min-w-0">
                      {tool.description}
                    </p>
                  </div>

                  <div className="mt-3 flex items-center gap-1 text-xs font-mono text-primary font-medium shrink-0">
                    <span>Launch Tool</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>)}
            </div>
          </div>}

        {/* Plain statement on privacy & reset control */}
        <div className="mt-8 pt-5 border-t border-border/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
            <p className="font-sans text-xs">
              No account required. Nothing leaves your browser. No leaderboards or pass/fail exams.
            </p>
          </div>

          <button type="button" onClick={clearProgress} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-destructive transition-colors text-xs font-mono self-start sm:self-auto min-h-[44px]">
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Clear my saved place</span>
          </button>
        </div>
      </div>
    </section>;
}